import type { ThemeId, ThemeState } from './types.js';

export function resolveTheme(theme: ThemeState['theme']): ThemeId {
  if (theme !== 'system') return theme;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyThemeAttributes(state: ThemeState): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  const resolved = resolveTheme(state.theme);
  el.setAttribute('data-theme', resolved);
  el.setAttribute('data-accent', state.accent);
  el.setAttribute('data-density', state.density);
  el.setAttribute('data-grid', state.grid);
  el.setAttribute('data-radius', state.radius);
  el.setAttribute('data-surface', state.surfaceStyle);
  el.setAttribute('data-shadow', state.shadow);
  el.setAttribute('data-typescale', state.typeScale);
  el.setAttribute('data-rendering', state.rendering);
  el.setAttribute('data-motion', state.motion);

  if (state.accent === 'custom' && state.customAccent !== undefined) {
    const { accent, ink, soft } = deriveCustomAccent(state.customAccent);
    el.style.setProperty('--vq-accent', accent);
    el.style.setProperty('--vq-accent-ink', ink);
    el.style.setProperty('--vq-accent-soft', soft);
  } else {
    el.style.removeProperty('--vq-accent');
    el.style.removeProperty('--vq-accent-ink');
    el.style.removeProperty('--vq-accent-soft');
  }
}

export function transitionTheme(apply: () => void): void {
  if (typeof document === 'undefined') {
    apply();
    return;
  }
  const el = document.documentElement;
  el.setAttribute('data-theme-transitioning', '');
  apply();
  window.setTimeout(() => {
    el.removeAttribute('data-theme-transitioning');
  }, 260);
}

// Convert #rrggbb → {h,s,l}; then shift L for ink (-12) and soft (+38, -30% sat)
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  const body = m?.[1];
  if (body === undefined) return { h: 0, s: 0, l: 50 };
  const int = parseInt(body, 16);
  const r = ((int >> 16) & 0xff) / 255;
  const g = ((int >> 8) & 0xff) / 255;
  const b = (int & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
        break;
      case g:
        h = ((b - r) / d + 2) * 60;
        break;
      case b:
        h = ((r - g) / d + 4) * 60;
        break;
    }
  }
  return { h, s: s * 100, l: l * 100 };
}

function hslToString(h: number, s: number, l: number): string {
  return `hsl(${h.toFixed(1)} ${clamp(s, 0, 100).toFixed(1)}% ${clamp(l, 0, 100).toFixed(1)}%)`;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function deriveCustomAccent(hex: string): {
  accent: string;
  ink: string;
  soft: string;
} {
  const { h, s, l } = hexToHsl(hex);
  return {
    accent: hslToString(h, s, l),
    ink: hslToString(h, s, l - 12),
    soft: hslToString(h, s - 30, l + 38),
  };
}
